// frontend/src/components/layout/TopBar.tsx
// Clean Public TopBar - For unauthenticated users only
// Professional, minimal design for landing pages

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import LanguageSelector from '../shared/LanguageSelector';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  normalizeLanguageCode,
} from '../../locales/languages';
import { saveLanguagePreference } from '../../utils/languageUtils';

const TopBar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const showDesktopPrimaryAction = pathname !== '/';

  const handleMobileLanguageChange = async (language: string) => {
    const liveLanguage = normalizeLanguageCode(language);
    if (!liveLanguage) return;

    await i18n.changeLanguage(liveLanguage);
    await saveLanguagePreference(liveLanguage);
  };

  useEffect(() => {
    const desktopQuery = window.matchMedia('(min-width: 900px)');
    const closeDesktopMenu = () => {
      if (desktopQuery.matches || window.innerWidth >= 900) {
        setMobileMenuOpen(false);
      }
    };

    closeDesktopMenu();
    desktopQuery.addEventListener('change', closeDesktopMenu);
    window.addEventListener('resize', closeDesktopMenu);
    window.addEventListener('orientationchange', closeDesktopMenu);
    return () => {
      desktopQuery.removeEventListener('change', closeDesktopMenu);
      window.removeEventListener('resize', closeDesktopMenu);
      window.removeEventListener('orientationchange', closeDesktopMenu);
    };
  }, []);

  return (
    <header className="topbar">
      <div className="topbar-container">
        {/* Left: Logo */}
        <div className="topbar-left">
          <Link to="/" className="topbar-logo">
            XPROJEX
          </Link>
        </div>

        {/* Center: Navigation (Desktop) - Public pages only */}
        <nav className="topbar-nav">
          <Link to="/features" className="topbar-link">{t('navbarFeatures')}</Link>
          <Link to="/how-it-works" className="topbar-link">{t('navbarHowItWorks')}</Link>
          <Link to="/pricing" className="topbar-link">{t('navbarPricing', 'Pricing')}</Link>
          <Link to="/help-center" className="topbar-link">{t('navbarFaqs')}</Link>
        </nav>

        {/* Right: Auth Actions + General Controls (Desktop only) */}
        <div className="topbar-right">
          {/* Auth Actions - Desktop only */}
          <button 
            className="topbar-secondary-action topbar-desktop-only"
            onClick={() => navigate('/login')}
          >
            {t('navbarLogin')}
          </button>
          {showDesktopPrimaryAction && (
            <button
              className="btn-primary btn-sm topbar-desktop-only"
              onClick={() => navigate('/concept-validator')}
              style={{
                backgroundColor: 'var(--primary-400)',
                color: 'white',
                border: 'none'
              }}
            >
              {t('navbarGetStarted')}
            </button>
          )}

          {/* Divider - Desktop only */}
          <div className="topbar-desktop-only" style={{ width: '1px', height: '24px', backgroundColor: 'var(--border-primary)', margin: '0 4px' }} />

          {/* General Controls - Desktop only */}
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="topbar-icon-btn topbar-desktop-only"
            title={theme === 'dark' ? t('themeLight') : t('themeDark')}
            aria-label={t('autogen.9b0eaf14')}
          >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          {/* Language Selector */}
          <div className="topbar-desktop-only">
            <LanguageSelector compact={true} showFlag={true} />
          </div>

          {/* Mobile Menu Toggle - Mobile only */}
          <button
            className="topbar-mobile-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={t('autogen.52092247')}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - Public Navigation Only */}
      {mobileMenuOpen && (
        <div className="topbar-mobile-menu">
          {/* Navigation Section */}
          <div className="mobile-menu-section-title">{t('autogen.cf03cf2e')}</div>
          <Link 
            to="/features" 
            className="topbar-mobile-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('navbarFeatures')}
          </Link>
          <Link 
            to="/how-it-works" 
            className="topbar-mobile-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('navbarHowItWorks')}
          </Link>
          <Link 
            to="/pricing" 
            className="topbar-mobile-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('navbarPricing', 'Pricing')}
          </Link>
          <Link 
            to="/help-center"
            className="topbar-mobile-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('navbarFaqs')}
          </Link>

          <div className="mobile-menu-divider"></div>

          {/* Actions Section */}
          <div className="mobile-menu-section-title">{t('navbarActions', 'Actions')}</div>
          <button 
            className="topbar-mobile-link mobile-menu-action"
            onClick={() => {
              navigate('/login');
              setMobileMenuOpen(false);
            }}
            style={{
              color: 'var(--text-secondary)',
              fontWeight: 500
            }}
          >
            {t('navbarLogin')}
          </button>

          <button 
            className="topbar-mobile-link mobile-menu-action"
            onClick={() => {
              navigate('/concept-validator');
              setMobileMenuOpen(false);
            }}
            style={{
              backgroundColor: 'var(--accent-solid)',
              color: 'white',
              fontWeight: 600
            }}
          >
            {t('navbarGetStarted')}
          </button>

          <div className="mobile-menu-divider"></div>
          
          {/* General Preferences - Always visible */}
          <div className="mobile-menu-section-title">{t('navbarGeneral', 'General')}</div>

          <button 
            className="topbar-mobile-link mobile-menu-action"
            onClick={toggleTheme}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            <span>{theme === 'dark' ? t('themeLight', 'Light Mode') : t('themeDark', 'Dark Mode')}</span>
          </button>

          <div className="mobile-menu-widget mobile-menu-lang">
            <span className="widget-label">{t('autogen.c7206476')}</span>
            <select
              value={normalizeLanguageCode(i18n.language) || DEFAULT_LANGUAGE}
              onChange={(event) => handleMobileLanguageChange(event.target.value)}
              className="mobile-lang-select"
              aria-label={t('changeLanguage') || 'Change Language'}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag ? `${lang.flag} ` : ''}{lang.nativeName}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </header>
  );
};

export default TopBar;
